import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import WizardCst from "@components/WizardCst";
import { WizardStep, WizardStepProps } from "@components/WizardCst/types";
import { StaticGroupWizardData } from "./types";
import {
  skipEnrollMode,
  validateEnrollMode,
  validateGroupInfo,
} from "./validation";
import GroupInfoStep from "./steps/GroupInfoStep";
import AddUsersStep from "./steps/AddUsersStep";
import EnrollStep from "./steps/EnrollStep";
import EnrollmentModeStep from "./steps/EnrollmentModeStep";
import { StaticGroupWizardProvider } from "./staticGroupWizardContext";

interface StaticGroupWizardProps {
  initialData: StaticGroupWizardData;
  backTo: string;
  onComplete: (data: StaticGroupWizardData) => void | Promise<void>;
  isCompleting?: boolean;
  title?: string;
  abandonMode?: "create" | "edit";
  groupId?: string;
}

const StaticGroupWizard: React.FC<StaticGroupWizardProps> = ({
  initialData,
  backTo,
  onComplete,
  isCompleting,
  title,
  abandonMode = "create",
  groupId,
}) => {
  const { t } = useTranslation("group-wizard-v2");

  const steps = useMemo<WizardStep<StaticGroupWizardData>[]>(
    () => [
      {
        key: "main-info",
        label: t("steps.mainInfo"),
        Component: GroupInfoStep,
        validate: validateGroupInfo,
      },
      {
        key: "add-users",
        label: t("steps.addUsers"),
        Component: AddUsersStep,
      },
      {
        key: "enroll",
        label: t("steps.enroll"),
        Component: (props: WizardStepProps<StaticGroupWizardData>) => (
          <EnrollStep {...props} />
        ),
      },
      {
        key: "enroll-mode",
        label: t("steps.enrollMode"),
        Component: (props: WizardStepProps<StaticGroupWizardData>) => (
          <EnrollmentModeStep
            {...props}
            hiddenEnrollTypes={
              abandonMode === "edit" ? undefined : ["AUTO", "BOTTOM"]
            }
          />
        ),
        validate: validateEnrollMode,
        skip: skipEnrollMode,
      },
    ],
    [t, abandonMode],
  );

  return (
    <AnimationFadeIn>
      <Box sx={{ py: 3, pr: 3 }}>
        <StaticGroupWizardProvider groupId={groupId}>
          <WizardCst<StaticGroupWizardData>
            title={title ?? t("titleCreate")}
            breadcrumb={<Breadcrumb sticky={false} />}
            backLink={{ label: t("backLink"), to: backTo }}
            steps={steps}
            initialData={initialData}
            onComplete={onComplete}
            abandonMode={abandonMode}
            navigationMode={abandonMode === "edit" ? "free" : "sequential"}
            abandonCopy={{
              title: String(
                t(
                  abandonMode === "edit"
                    ? "abandon.edit-title"
                    : "abandon.create-title",
                ),
              ),
              description:
                abandonMode === "edit"
                  ? undefined
                  : String(t("abandon.create-description")),
            }}
            nextLabel={t("next")}
            completeLabel={t("confirm")}
            backLabel={t("back")}
            resetLabel={t("reset")}
            isCompleting={isCompleting}
            resetConfirm={{
              title: t("resetConfirm.title"),
              description: t("resetConfirm.description"),
              confirmLabel: t("resetConfirm.confirm"),
              cancelLabel: t("resetConfirm.cancel"),
            }}
          />
        </StaticGroupWizardProvider>
      </Box>
    </AnimationFadeIn>
  );
};

export default StaticGroupWizard;
